import React, {memo} from 'react'
import {isEmpty, keys} from 'lodash'
import {Button, Form, Header, List, Message, Modal} from 'semantic-ui-react'
import {withErrorHandling, withHelmet, withMeData, withPropsPresent, withUserData, withUsersData} from '../../hoc'
import {atUserLimit, goBack} from '../../util'
import {sendInvitation, upsertUser} from '../../backend'
import {useSafeState} from '../../hook'

const getAccountId = (account) => {
  return account.id ? account.id : account
}

const isUserPresent = (users, email) => {
  return users.some((u) => u.email.toLowerCase() === email.toLowerCase())
}

const _UpsertUser = (
  {
    invite,
    user,
    history,
    navigate,
    users = [],
    sendInvitation,
    upsertUser,
    mutateUsers,
    mutateUser,
    mutateMe,
    basePath = '/c/account/users',
    goBack
  }
) => {

  const [state, setState] = useSafeState({
    invite,
    email: '',
    first: '',
    last: '',
    isAdmin: false,
    isBillingOwner: false,
    submitted: false,
    errors: {},
    ...(user || {}),
    user: (user || {})
  })

  const validate = ({email, first, last, errors = {}}) => {
    if (!email) {
      errors['user.email.required'] = 'Email required'
    } else {
      delete errors['user.email.required']
      if (email.length > 150) {
        errors['user.email.exceeds.max.length'] = 'First name cannot exceed 150 characters'
      } else {
        delete errors['user.email.exceeds.max.length']
        if (isUserPresent(users, email)) {
          errors['user.taken'] = 'User already present. Change email and try again'
        } else {
          delete errors['user.taken']
        }
      }
    }
    if (!first || isEmpty(first.trim())) {
      errors['user.first.required'] = 'First name required'
    } else {
      delete errors['user.first.required']
      if (first.length > 150) {
        errors['user.first.exceeds.max.length'] = 'First name cannot exceed 150 characters'
      } else {
        delete errors['user.first.exceeds.max.length']
      }
    }
    if (!last || isEmpty(last.trim())) {
      errors['user.last.required'] = 'Last name required'
    } else {
      delete errors['user.last.required']
      if (last.length > 150) {
        errors['user.last.exceeds.max.length'] = 'Last name cannot exceed 150 characters'
      } else {
        delete errors['user.last.exceeds.max.length']
      }
    }
    return errors
  }

  const onInputChange = (e) => {
    let updatedState = {...state, ...{[e.target.name]: e.target.value}}
    let errors = {}
    if (state.submitted) {
      errors = validate(updatedState)
      updatedState = {...updatedState, ...{errors}}
    }
    setState(updatedState)
  }

  const onClose = () => {
    goBack(history, navigate, basePath)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)

    if (keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }

    setState({...state, ...{submitting: true}})
    if (invite) {
      await sendInvitation(state)
    } else {
      await upsertUser(state)
    }
    setState({...state, ...{submitting: false}})

    mutateUsers()
    mutateUser()
    mutateMe()

    if (!state.id) {
      navigate(basePath)
    } else {
      goBack(history, navigate, basePath)
    }
  }

  const {email, first, last, errors, submitting} = state
  const hasUserTakenError = keys(errors).some((k) => k.indexOf('user.taken') > -1)
  const hasEmailError = keys(errors).some((k) => k.indexOf('user.email') > -1)
  const hasFirstError = keys(errors).some((k) => k.indexOf('user.first') > -1)
  const hasLastError = keys(errors).some((k) => k.indexOf('user.last') > -1)
  const hasErrors = hasUserTakenError || hasEmailError || hasFirstError || hasLastError

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose} data-testid='user-upsert-modal'>
      <Header content={'Invite User'}/>
      <Modal.Content>
        <Form error={hasErrors}>
          <Form.Field error={hasEmailError || hasUserTakenError} required>
            <label>Email</label>
            <Form.Input placeholder='Enter user email'
                        name='email'
                        value={email}
                        data-testid='user-email-textbox'
                        onChange={onInputChange}/>
          </Form.Field>
          <Form.Field error={hasFirstError} required>
            <label>First</label>
            <Form.Input placeholder='Enter first name'
                        name='first'
                        value={first}
                        autoComplete='false'
                        data-testid='user-first-textbox'
                        onChange={onInputChange}/>
          </Form.Field>
          <Form.Field error={hasLastError} required>
            <label>Last</label>
            <Form.Input placeholder='Enter last name'
                        name='last'
                        value={last}
                        autoComplete='false'
                        data-testid='user-last-textbox'
                        onChange={onInputChange}/>
          </Form.Field>
          <Message error data-testid='user-errors'>
            <Message.Content>
              <List bulleted>
                {
                  keys(errors).map((key) => (
                    <List.Item key={key} data-test='user-error'>{errors[key]}</List.Item>
                  ))
                }
              </List>
            </Message.Content>
          </Message>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='user-cancel-button'>Cancel</Button>
        <Button primary onClick={onSubmit}
                data-testid='user-submit-button'
                loading={submitting}>{invite ? 'Invite' : 'Update'}</Button>
      </Modal.Actions>
    </Modal>
  )
}

let UpsertUser = (props) => {
  if (atUserLimit(props.me)) {
    props.navigate('/c/account/users')
    return null
  }

  const {handleError} = props
  props = {...props, goBack, sendInvitation: handleError(sendInvitation), upsertUser: handleError(upsertUser)}
  return (<_UpsertUser {...props}/>)
}

UpsertUser = withHelmet(UpsertUser, 'Invite User')
UpsertUser = withPropsPresent(UpsertUser, ['me', 'users', (props) => !props.userId || !!props.user])
UpsertUser = withMeData(UpsertUser)
UpsertUser = withUserData(UpsertUser)
UpsertUser = withUsersData(UpsertUser)
UpsertUser = withErrorHandling(UpsertUser)
UpsertUser = memo(UpsertUser)

export {_UpsertUser, UpsertUser, isUserPresent, getAccountId}

export default UpsertUser